import * as ExcelJS from "exceljs";
import {
  fetchStudentVideoSubmissionsRaw,
  mapApiSubmissionToSubmission,
  STATUS_API_VALUE,
  toApiDate,
  type ApiStudentVideoSubmission,
  type StudentVideoSubmissionFilters,
} from "@/projects/lib/submissionsApi";
import type { Submission } from "@/projects/lib/store";

export type ReportKey =
  | "date"
  | "status"
  | "teacher"
  | "pending"
  | "reward"
  | "notproper";

export type ReportDef = {
  key: ReportKey;
  label: string;
  /** Fixed status filter for this report tab (omit = all statuses). */
  status?: string;
};

export const REPORTS: ReportDef[] = [
  { key: "date", label: "Date-wise Submission Report" },
  { key: "status", label: "Status-wise Submission Report" },
  { key: "teacher", label: "Teacher-wise Verification Report" },
  {
    key: "pending",
    label: "Pending Verification Report",
    status: STATUS_API_VALUE["Assigned for Verification"],
  },
  {
    key: "reward",
    label: "Ready for Reward Report",
    status: STATUS_API_VALUE["Ready for Reward"],
  },
  {
    key: "notproper",
    label: "Not Proper Report",
    status: STATUS_API_VALUE["Not Proper"],
  },
];

export type ReportFilters = {
  teacherUserId?: string;
  fromDate?: string; // yyyy-mm-dd (HTML date)
  toDate?: string;
};

export type ReportResult = {
  rows: Submission[];
  /** Full API response records — used for export (all fields/values). */
  raw: ApiStudentVideoSubmission[];
};

/** Build query params for the selected left-tab report. */
export function buildReportFilters(
  reportKey: ReportKey,
  filters: ReportFilters = {}
): StudentVideoSubmissionFilters {
  const report = REPORTS.find(r => r.key === reportKey);
  const params: StudentVideoSubmissionFilters = {};

  if (report?.status) {
    params.status = report.status;
  }
  if (filters.teacherUserId && filters.teacherUserId !== "all") {
    params.assigned_teacher = filters.teacherUserId;
  }
  if (filters.fromDate) {
    params.from_date = toApiDate(filters.fromDate);
  }
  if (filters.toDate) {
    params.to_date = toApiDate(filters.toDate);
  }

  return params;
}

/**
 * Fetch report rows for a left-tab key.
 * Uses GET /rajnagar_gyanmahostav/student-video-submissions/ with tab filters.
 */
export async function fetchReport(
  reportKey: ReportKey,
  filters: ReportFilters = {}
): Promise<ReportResult> {
  const raw = await fetchStudentVideoSubmissionsRaw(
    buildReportFilters(reportKey, filters)
  );
  return {
    raw,
    rows: raw.map(mapApiSubmissionToSubmission),
  };
}

const LINK_FIELDS = new Set([
  "video_attachment",
  "video_url",
  "videoUrl",
]);

function cellText(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function resolveUrl(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  // Relative media path → absolute against API host
  try {
    const base = String(import.meta.env.VITE_API_BASE_URL || "").replace(
      /\/api\/?$/,
      ""
    );
    if (base && trimmed.startsWith("/")) {
      return `${base}${trimmed}`;
    }
    if (base) {
      return `${base}/${trimmed.replace(/^\//, "")}`;
    }
  } catch {
    /* ignore */
  }
  return trimmed;
}

/** Short label for link cells — keeps rows compact; full URL stays on hyperlink. */
function shortLinkLabel(
  href: string,
  row: ApiStudentVideoSubmission,
  key: string
): string {
  const fileName =
    (typeof row.video_file_name === "string" && row.video_file_name) ||
    (typeof row.file_name === "string" && row.file_name) ||
    "";
  if (fileName) {
    return fileName.length > 28 ? `${fileName.slice(0, 25)}...` : fileName;
  }
  try {
    const path = new URL(href).pathname;
    const name = decodeURIComponent(path.split("/").filter(Boolean).pop() ?? "");
    if (name) {
      return name.length > 28 ? `${name.slice(0, 25)}...` : name;
    }
  } catch {
    /* ignore */
  }
  const raw = cellText(row[key]);
  return raw.length > 28 ? `${raw.slice(0, 25)}...` : raw || "Open video...";
}

function collectHeaders(rawRows: ApiStudentVideoSubmission[]): string[] {
  const headers: string[] = [];
  const seen = new Set<string>();
  for (const row of rawRows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        headers.push(key);
      }
    }
  }
  return headers;
}

/**
 * Export every API response field.
 * - Header row: bold text on separated grey background
 * - video_attachment (and video_url): clickable hyperlink
 */
export async function exportReportCsv(
  rawRows: ApiStudentVideoSubmission[],
  reportKey: ReportKey
): Promise<void> {
  if (!rawRows.length) {
    return;
  }

  const headers = collectHeaders(rawRows);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rajnagar Gyanmahostav";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Report", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  // Header row — separated grey background
  const headerRow = sheet.addRow(headers);
  headerRow.height = 22;
  headerRow.eachCell(cell => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9D9D9" },
    };
    cell.font = { bold: true, color: { argb: "FF333333" } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = {
      top: { style: "thin", color: { argb: "FFB0B0B0" } },
      left: { style: "thin", color: { argb: "FFB0B0B0" } },
      bottom: { style: "thin", color: { argb: "FFB0B0B0" } },
      right: { style: "thin", color: { argb: "FFB0B0B0" } },
    };
  });

  for (const row of rawRows) {
    const values = headers.map(key => cellText(row[key]));
    const dataRow = sheet.addRow(values);

    headers.forEach((key, index) => {
      const cell = dataRow.getCell(index + 1);
      cell.alignment = {
        vertical: "middle",
        wrapText: !LINK_FIELDS.has(key),
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FFE0E0E0" } },
        left: { style: "thin", color: { argb: "FFE0E0E0" } },
        bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
        right: { style: "thin", color: { argb: "FFE0E0E0" } },
      };

      if (LINK_FIELDS.has(key)) {
        const href = resolveUrl(row[key]);
        if (href) {
          const label = shortLinkLabel(href, row, key);
          cell.value = {
            text: label,
            hyperlink: href,
            tooltip: href,
          };
          cell.font = {
            color: { argb: "FF0563C1" },
            underline: true,
          };
        }
      }
    });
  }

  // Auto column widths (capped); link columns stay narrow
  headers.forEach((key, i) => {
    if (LINK_FIELDS.has(key)) {
      sheet.getColumn(i + 1).width = 32;
      return;
    }
    let max = key.length;
    for (const row of rawRows.slice(0, 50)) {
      const len = cellText(row[key]).length;
      if (len > max) {
        max = len;
      }
    }
    sheet.getColumn(i + 1).width = Math.min(48, Math.max(12, max + 2));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `report-${reportKey}-${Date.now()}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export function getReportErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
  }
  return fallback;
}
